import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect } from "react";
import createVariablesMentionExtension, {
  parseMentionsFromText,
} from "../../lib/mention-extension";
import { type VariableDefinition, useUnavailableVariableIds } from "@babackapp/variables-registry";
import { useTranslation } from "react-i18next";

interface LinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentHref?: string;
  onSave: (href: string) => void;
  variables: VariableDefinition[];
}

export function LinkModal({
  open,
  onOpenChange,
  currentHref = "",
  onSave,
  variables,
}: LinkModalProps) {
  const { t } = useTranslation("editor-blocks-library");
  const unavailableVariableIds = useUnavailableVariableIds();
  const editor = useEditor({
    extensions: [
      Document,
      createVariablesMentionExtension(variables, unavailableVariableIds),
      Paragraph,
      Text,
    ],
    content: parseMentionsFromText(currentHref ?? "", variables),
    editorProps: {
      attributes: {
        class:
          "min-h-9 px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0",
        style: "white-space: pre-wrap;",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const doc = parseMentionsFromText(currentHref ?? "", variables);
    if (
      editor.getJSON &&
      JSON.stringify(editor.getJSON()) !== JSON.stringify(doc)
    ) {
      editor.commands.setContent(doc, { emitUpdate: false });
    }
  }, [editor, currentHref, open, variables]);

  const handleSave = () => {
    const text = editor?.getText() ?? "";
    onSave(text.trim());
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to currentHref when closing without saving
    if (editor) {
      editor.commands.setContent(
        parseMentionsFromText(currentHref ?? "", variables),
        {
          emitUpdate: false,
        },
      );
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              "editorBlocksLibrary.blocks.paragraph.linkModal.title",
              "Add Link",
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t(
              "editorBlocksLibrary.blocks.paragraph.linkModal.urlLabel",
              "URL",
            )}
          </label>
          <EditorContent editor={editor} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t(
              "editorBlocksLibrary.blocks.paragraph.linkModal.cancel",
              "Cancel",
            )}
          </Button>
          <Button onClick={handleSave}>
            {t("editorBlocksLibrary.blocks.paragraph.linkModal.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
